// /dashboard - role-aware landing for the AiLys partner portal.
//
// Each role gets a distinct view per ADR-0022 cascade:
// - client: pointer to main client portal (this portal is partner-side)
// - partner_owner: org overview snapshot
// - ailys_staff: pilot list summary
// - ailys_admin: admin entry points
// Agent roles (in_training, certified) defined for forward compat;
// activated in Phase 6.3.
//
// Phase 6.1 Chunk e.

import { useEffect, useState } from 'react';
import { PortalLayout } from '../components/PortalLayout';
import { useAuth } from '../hooks/useAuth';
import { useLang, fmt } from '../i18n';
import {
  Card, CardLabel, CardValue, CardDescription,
  SkeletonCard,
  headingStyle, mutedStyle, linkStyle,
} from '../components/ui';

const MS_PER_DAY = 86_400_000;

function ClientView() {
  const { t } = useLang();
  return (
    <Card>
      <CardLabel>{t.dashboard.wrongPortalTitle}</CardLabel>
      <p style={mutedStyle}>
        {t.dashboard.wrongPortalMsg} <code>my.ailysagency.ca</code>{' '}
        {t.dashboard.wrongPortalMsg2}
      </p>
    </Card>
  );
}

interface DashboardData {
  clientCount: number;
  ytdTotal: number;
  tier: string;
  certStatus: string;
  certExpires: string | null;
}

function useDashboardData(): { data: DashboardData | null; loading: boolean } {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [clientsRes, commissionsRes, settingsRes] = await Promise.all([
          fetch('/api/partner/clients', { credentials: 'same-origin' }),
          fetch('/api/partner/commissions', { credentials: 'same-origin' }),
          fetch('/api/partner/settings', { credentials: 'same-origin' }),
        ]);

        let clientCount = 0;
        if (clientsRes.ok) {
          const cj = (await clientsRes.json()) as { rows?: unknown[] };
          clientCount = cj.rows?.length ?? 0;
        }

        let ytdTotal = 0;
        if (commissionsRes.ok) {
          const comj = (await commissionsRes.json()) as { ytd_t4a_total?: number };
          ytdTotal = comj.ytd_t4a_total ?? 0;
        }

        let tier = 'starter';
        let certStatus = 'active';
        let certExpires: string | null = null;
        if (settingsRes.ok) {
          const sj = (await settingsRes.json()) as {
            qualification_tier?: string;
            cert_status?: string;
            cert_expires_at?: string;
          };
          tier = sj.qualification_tier ?? 'starter';
          certStatus = sj.cert_status ?? 'active';
          certExpires = sj.cert_expires_at ?? null;
        }

        if (!cancelled) {
          setData({ clientCount, ytdTotal, tier, certStatus, certExpires });
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { data, loading };
}

function useFormatTier() {
  const { t } = useLang();
  return (tier: string): string => {
    const map: Record<string, string> = {
      pending: t.dashboard.tierPending,
      starter: t.dashboard.tierStarter,
      silver: t.dashboard.tierSilver,
      gold: t.dashboard.tierGold,
      elite: t.dashboard.tierElite,
    };
    return map[tier] ?? tier;
  };
}

function useFormatCert() {
  const { t } = useLang();
  return (cert: string): string => {
    const map: Record<string, string> = {
      in_training: t.dashboard.certInTraining,
      active: t.dashboard.certActive,
      expired: t.dashboard.certExpired,
      revoked: t.dashboard.certRevoked,
    };
    return map[cert] ?? cert;
  };
}

function useCertSubtext() {
  const { t } = useLang();
  return (status: string, expires: string | null): string => {
    if (status === 'expired') return t.dashboard.certRenewRequired;
    if (status === 'revoked') return t.dashboard.certContactSupport;
    if (status === 'in_training') return t.dashboard.certCompleteTraining;
    if (expires) {
      const days = Math.ceil((new Date(expires).getTime() - Date.now()) / MS_PER_DAY);
      if (days > 0) return fmt(t.dashboard.certRecertifyDays, { days });
      return t.dashboard.certRenewalOverdue;
    }
    return t.dashboard.certRecertifyBefore;
  };
}

function PartnerOwnerView() {
  const { t } = useLang();
  const { data, loading: dataLoading } = useDashboardData();
  const formatTier = useFormatTier();
  const formatCert = useFormatCert();
  const certSubtext = useCertSubtext();

  return (
    <>
      <h1 style={headingStyle}>
        {t.dashboard.partnerOverview}
      </h1>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        {dataLoading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : (
          <>
            <Card>
              <CardLabel>{t.dashboard.activeClients}</CardLabel>
              <CardValue>{data?.clientCount ?? 0}</CardValue>
              <CardDescription>{t.dashboard.activeClientsDesc}</CardDescription>
            </Card>

            <Card>
              <CardLabel>{t.dashboard.ytdCommissions}</CardLabel>
              <CardValue>
                {`$${(data?.ytdTotal ?? 0).toFixed(2)}`}
              </CardValue>
              <CardDescription>{t.dashboard.ytdCommissionsDesc}</CardDescription>
            </Card>

            <Card>
              <CardLabel>{t.dashboard.tier}</CardLabel>
              <CardValue>{formatTier(data?.tier ?? 'starter')}</CardValue>
              <CardDescription>{t.dashboard.tierDesc}</CardDescription>
            </Card>

            <Card>
              <CardLabel>{t.dashboard.certification}</CardLabel>
              <CardValue>{formatCert(data?.certStatus ?? 'active')}</CardValue>
              <CardDescription>
                {certSubtext(data?.certStatus ?? 'active', data?.certExpires ?? null)}
              </CardDescription>
            </Card>
          </>
        )}
      </div>

      <Card>
        <CardLabel>{t.dashboard.quickLinks}</CardLabel>
        <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0 0', lineHeight: 1.8 }}>
          <li>
            <a href="/clients" style={linkStyle}>
              {t.dashboard.linkClients}
            </a>
          </li>
          <li>
            <a href="/commissions" style={linkStyle}>
              {t.dashboard.linkCommissions}
            </a>
          </li>
          <li>
            <a href="/settings" style={linkStyle}>
              {t.dashboard.linkSettings}
            </a>
          </li>
          <li>
            <a
              href="https://www.ailysagency.ca/help/partner-portal-overview"
              style={linkStyle}
              target="_blank"
              rel="noreferrer"
            >
              {t.dashboard.linkHelp}
            </a>
          </li>
        </ul>
      </Card>
    </>
  );
}

function InternalRoleView({ title, body }: { title: string; body: string }) {
  return (
    <>
      <h1 style={headingStyle}>
        {title}
      </h1>
      <p style={mutedStyle}>
        {body}{' '}
        <a
          href="https://www.ailysagency.ca/admin"
          style={linkStyle}
        >
          ailysagency.ca/admin
        </a>
      </p>
    </>
  );
}

export default function Dashboard() {
  const auth = useAuth();
  const { t } = useLang();

  if (auth.loading) {
    return (
      <PortalLayout>
        <div style={{ padding: '32px' }}>
          <div style={mutedStyle}>{t.common.loading}</div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div style={{ padding: '32px', maxWidth: '900px' }}>
        {auth.role === 'client' && <ClientView />}
        {(auth.role === 'partner_owner' ||
          auth.role === 'agent_certified' ||
          auth.role === 'agent_in_training') && <PartnerOwnerView />}
        {auth.role === 'ailys_staff' && (
          <InternalRoleView
            title={t.dashboard.staffOverview}
            body={t.dashboard.staffOverviewMsg}
          />
        )}
        {auth.role === 'ailys_admin' && (
          <InternalRoleView
            title={t.dashboard.adminOverview}
            body={t.dashboard.adminOverviewMsg}
          />
        )}
      </div>
    </PortalLayout>
  );
}
